import compact from 'lodash/compact';
import noop from 'lodash/noop';
import { useMemo } from 'react';

import { MagistralDrugRawMaterial } from '@dreryk/drerykweb-medicine-api';

import { Translate } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { FormsTemplates } from '~libs.desktop.shared.user-templates.forms';

import { magistralDrugTemplatesTranslationKeys, prescriptionsTranslationKeys } from '../../../../../../translations';
import { MagistralDrugTemplateInsert } from '../MagistralDrugReadOnly.types';

import { CheckboxCell } from './cells/CheckboxCell';
import { TooltipCell } from './cells/TooltipCell';
import { mapMedicineApiMagistralDrugRawMaterialToRawMaterial } from './MagistralDrugTemplateRawMaterialsReadOnlyTable.utils';

// eslint-disable-next-line max-lines-per-function
export const useMagistralDrugTemplateRawMaterialsReadOnlyTableColumns = (
   onInsert: MagistralDrugTemplateInsert | undefined
): TableColumnConfiguration<MagistralDrugRawMaterial>[] => {
   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnsWidths = createDynamicColumnsWidth({
         ingredientName: '100%',
         rpw: '47px',
         quantityAbbreviation: '97px',
         quantity: '71px',
         unit: '55px',
         excessive: '37px',
         options: onInsert ? '91px' : '0px',
      });
      return compact([
         {
            name: 'ingredientName',
            accessor: 'name',
            label: magistralDrugTemplatesTranslationKeys.rawMaterialsTable.ingredientName,
            width: columnsWidths.ingredientName,

            renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) => (
               <TooltipCell testId={'ingredientName'}>{bean.name}</TooltipCell>
            ),
            sortingDisabled: true,
         },
         {
            name: 'rpw',
            accessor: 'rpw',
            width: columnsWidths.rpw,
            label: magistralDrugTemplatesTranslationKeys.rawMaterialsTable.rpw,

            renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) => (
               <CheckboxCell selected={bean.rpw} testId={'rpw'} />
            ),
            sortingDisabled: true,
         },
         {
            name: 'quantityAbbreviation',
            accessor: 'quantityAbbreviation',
            width: columnsWidths.quantityAbbreviation,
            label: magistralDrugTemplatesTranslationKeys.rawMaterialsTable.quantityAbbreviation,

            renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) => (
               <TooltipCell testId={'quantityAbbreviation'}>
                  {bean.quantityAbbreviation ? (
                     <Translate>
                        {prescriptionsTranslationKeys.quantityAbbreviation[bean.quantityAbbreviation]}
                     </Translate>
                  ) : null}
               </TooltipCell>
            ),
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'quantity',
            accessor: 'quantity',
            width: columnsWidths.quantity,
            label: magistralDrugTemplatesTranslationKeys.rawMaterialsTable.quantity,

            renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) => (
               <TooltipCell testId={'quantity'}>{bean.quantity ?? null}</TooltipCell>
            ),
            sortingDisabled: true,
         },
         {
            name: 'unit',
            accessor: 'quantityUnit',
            width: columnsWidths.unit,
            label: magistralDrugTemplatesTranslationKeys.rawMaterialsTable.unit,

            renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) =>
               bean.quantityUnit ? (
                  <TooltipCell testId={'unit'}>
                     <Translate>{prescriptionsTranslationKeys.quantityUnitShortcut[bean.quantityUnit]}</Translate>
                  </TooltipCell>
               ) : null,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'excessive',
            accessor: 'highDoseAlert',
            width: columnsWidths.excessive,
            label: magistralDrugTemplatesTranslationKeys.rawMaterialsTable.excessive,

            renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) => (
               <CheckboxCell selected={bean.highDoseAlert} testId={'excessive'} />
            ),
            sortingDisabled: true,
         },
         onInsert
            ? {
                 name: 'options',
                 accessor: noop,
                 width: columnsWidths.options,

                 renderer: ({ bean }: ValueRendererProps<MagistralDrugRawMaterial>) => (
                    <FormsTemplates.ReadOnlyInsertLink
                       testId={`ingredient-${bean.id!}`}
                       onClick={() => {
                          onInsert('ingredient', mapMedicineApiMagistralDrugRawMaterialToRawMaterial(bean));
                       }}
                    />
                 ),
                 sortingDisabled: true,
              }
            : undefined,
      ]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
};
